import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Alert } from '@/components/ui';
import type { WorkIdDetail } from '../../../services/workIdService';
import { routineTaskService } from '../../../services/routineTaskService';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import ResponsiveModal from '../../../components/ResponsiveModal';

type TaskWithType = WorkIdDetail['tasks'][number];

interface WorkIdTaskReopenModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdTaskReopenModal: React.FC<WorkIdTaskReopenModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const closedTasks = useMemo(() => {
    if (!workId?.tasks) return [];
    return workId.tasks.filter(
      (t) =>
        (t.type === 'Routine' || t.type === 'Quantified') &&
        t.status?.toUpperCase() === 'CLOSED'
    ) as TaskWithType[];
  }, [workId?.tasks]);

  useEffect(() => {
    if (visible) {
      setReason('');
      setError(null);
      setValidationError(null);
      if (closedTasks.length === 1) {
        setSelectedTaskId(closedTasks[0].id);
      } else if (closedTasks.length > 1) {
        setSelectedTaskId(closedTasks[0]?.id ?? null);
      } else {
        setSelectedTaskId(null);
      }
    }
  }, [visible, closedTasks]);

  const selectedTask = closedTasks.find((t) => t.id === selectedTaskId) ?? closedTasks[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError('Reason is required.');
      return;
    }
    if (!selectedTask) return;
    setValidationError(null);
    setError(null);
    setSubmitting(true);
    try {
      if (selectedTask.type === 'Routine') {
        await routineTaskService.updateTaskStatus(selectedTask.id, 'ACTIVE', trimmed);
      } else if (selectedTask.type === 'Quantified') {
        await quantifiedTaskService.reopenTask(selectedTask.id, {
          taskId: selectedTask.id,
          comments: trimmed,
        });
      } else {
        setError('Only Routine or Quantified tasks can be reopened from here.');
        setSubmitting(false);
        return;
      }
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reopen task');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Reopen task - Work ID ${workId.workIdentificationId ?? workId.id}`;

  if (closedTasks.length === 0) {
    return (
      <ResponsiveModal
        isOpen={visible}
        onClose={onClose}
        title={title}
        size="medium"
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <p className="tw-text-content-muted tw-mb-0">
          No closed Routine or Quantified tasks are linked to this Work ID. There is nothing to reopen.
        </p>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Reopening…' : 'Reopen task'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger" className="tw-py-2">
            {error}
          </Alert>
        )}
        {closedTasks.length > 1 && (
          <Form.Group className="tw-mb-3">
            <Form.Label>Task to reopen</Form.Label>
            <Form.Select
              value={selectedTaskId ?? ''}
              onChange={(e) => setSelectedTaskId(parseInt(e.target.value, 10) || null)}
            >
              {closedTasks.map((t) => (
                <option key={t.id} value={t.id}>
                  #{t.id} – {t.type} – {t.taskNumber ?? t.name ?? 'Task'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        <Form.Group className="tw-mb-0">
          <Form.Label>Reason <span className="tw-text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setValidationError(null);
            }}
            isInvalid={!!validationError}
            placeholder="Enter reason for reopening this task…"
          />
          {validationError && (
            <Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>
          )}
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdTaskReopenModal;
